import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Zoom,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import axiosInstance from "../services/axiosInstance";
import { MASTER_CONTROLS_UPLOAD, MASTER_PARAMS_UPLOAD, MASTER_HELPER_UPDATE } from "../shared/constants/apiEndpoints";
import AgGridTable from "../components/table/AgGridTable";
import SkeletonLoader from "../components/skeleton_loader";

const MasterFile: React.FC = () => {
  const [controlsFile, setControlsFile] = useState<File | null>(null);
  const [paramsFile, setParamsFile] = useState<File | null>(null);
  const [rulesFile, setRulesFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<"controls" | "params">("controls");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [whatsNew, setWhatsNew] = useState("");
  const [version, setVersion] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  const [controlsFiles, setControlsFiles] = useState<any[]>([]);
  const [paramsFiles, setParamsFiles] = useState<any[]>([]);
  const masterFiles = React.useMemo(() => [...controlsFiles, ...paramsFiles], [controlsFiles, paramsFiles]);
  
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const activeColor = isDarkMode ? "#818CF8" : "#3A2ADA";
  const activeHoverColor = isDarkMode ? "#6366F1" : "#2e1ec7";
  const containerBg = isDarkMode ? "#18181B" : "#ffffff";
  const borderClr = isDarkMode ? "rgba(255, 255, 255, 0.12)" : "#EAECF0";
  const primaryText = isDarkMode ? "#F4F4F5" : "#101828";
  const secondaryText = isDarkMode ? "#A1A1AA" : "#475467";

  const fetchControlsFiles = async () => {
    try {
      const res = await axiosInstance.get(`${MASTER_HELPER_UPDATE}?type=controls`);
      const payload = res.data?.data ?? res.data;
      if (Array.isArray(payload)) {
        setControlsFiles(payload);
      } else if (payload && typeof payload === "object") {
        setControlsFiles([payload]);
      } else {
        setControlsFiles([]);
      }
    } catch (error) {
      console.error("Failed to fetch controls files:", error);
      setControlsFiles([]);
    }
  };

  const fetchParamsFiles = async () => {
    try {
      const res = await axiosInstance.get(`${MASTER_HELPER_UPDATE}?type=params`);
      const payload = res.data?.data ?? res.data;
      if (Array.isArray(payload)) {
        setParamsFiles(payload);
      } else if (payload && typeof payload === "object") {
        setParamsFiles([payload]);
      } else {
        setParamsFiles([]);
      }
    } catch (error) {
      console.error("Failed to fetch params files:", error);
      setParamsFiles([]);
    }
  };

  const fetchMasterFiles = async () => {
    setLoading(true);
    await Promise.all([fetchControlsFiles(), fetchParamsFiles()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchMasterFiles();
  }, []);

  // Drag and drop callback for controls / params files
  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      if (uploadType === "controls") {
        setControlsFile(acceptedFiles[0]);
      } else {
        setParamsFile(acceptedFiles[0]);
      }
      setIsModalOpen(true);
    }
  };

  const { open: openFileBrowser } = useDropzone({
    onDrop,
    noClick: true,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
  });

  // secondary file dropzone (rules JSON)
  const onRulesDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setRulesFile(acceptedFiles[0]);
    }
  };

  const {
    getRootProps: getRulesRootProps,
    getInputProps: getRulesInputProps,
    isDragActive: isRulesDragActive,
    open: openRulesBrowser
  } = useDropzone({
    onDrop: onRulesDrop,
    noClick: true,
    multiple: false,
    accept: {
      "application/json": [".json"],
    },
  });

  const handleCloseModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
    setControlsFile(null);
    setParamsFile(null);
    setRulesFile(null);
    setWhatsNew("");
    setVersion("");
    setUploadType("controls");
  };

  const handleRemoveFile = () => {
    if (uploadType === "controls") {
      setControlsFile(null);
    } else {
      setParamsFile(null);
    }
  };

  const handleSave = async () => {
    if (!whatsNew.trim()) {
      toast.error("Please fill in what is new in this version.");
      return;
    }
    if (!version.trim()) {
      toast.error("Please enter the version.");
      return;
    }

    if (uploadType === "controls") {
      if (!controlsFile) {
        toast.error("Please upload a controls file.");
        return;
      }
    } else {
      if (!paramsFile) {
        toast.error("Please upload a params file.");
        return;
      }
      if (!rulesFile) {
        toast.error("Please upload a rules JSON file.");
        return;
      }
    }

    setIsSaving(true);
    try {
      if (uploadType === "controls") {
        const formData1 = new FormData();
        formData1.append("controls", controlsFile!);
        formData1.append("version", version);

        const response = await axiosInstance.post(MASTER_CONTROLS_UPLOAD, formData1, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.success(response?.data?.message || "Controls file uploaded successfully.");
        await fetchControlsFiles();
      } else {
        const formData1 = new FormData();
        formData1.append("params", paramsFile!);
        formData1.append("version", version);
        formData1.append("rules", rulesFile!);

        const response = await axiosInstance.post(MASTER_PARAMS_UPLOAD, formData1, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        toast.success(response?.data?.message || "Params and rules files uploaded successfully.");
        await fetchParamsFiles();
      }

      handleCloseModal();
    } catch (error: any) {
      console.error(error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to upload masterfile. Please try again.";
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRollBack = () => {
    toast.info("Roll back feature coming soon.");
    handleMenuClose();
  };

  // Safe rendering of dates
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, '0');
    const monthName = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day} ${monthName} ${year}, ${hours}:${minutes}:${seconds}`;
  };

  const columnDefs = [
    {
      headerName: "Upload ID",
      field: "id",
      minWidth: 280,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = params.value || params.data?.upload_id || params.data?.UploadID || "-";
        return (
          <Tooltip title={val} arrow>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                color: isDarkMode ? "#F8FAFC" : "#1E293B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {val}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "File name",
      field: "file_name",
      minWidth: 320,
      flex: 2,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val =
          params.data?.file_name ||
          params.data?.fileName ||
          params.data?.name ||
          (params.data?.s3_file_path ? params.data.s3_file_path.split("/").pop() : "-");
        return (
          <Tooltip title={val} arrow>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: isDarkMode ? "#F8FAFC" : "#1E293B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {val}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "What's new",
      field: "whats_new",
      minWidth: 180,
      flex: 1,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = params.value || params.data?.whatsNew || params.data?.description || "-";
        return (
          <Typography variant="body2" sx={{ color: isDarkMode ? "#CBD5E1" : "#475569" }}>
            {val}
          </Typography>
        );
      },
    },
    {
      headerName: "Version",
      field: "version",
      minWidth: 120,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = params.value || "-";
        return (
          <Chip
            label={val}
            size="small"
            variant="outlined"
            sx={{
              fontFamily: "'Fira Code', monospace",
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: "6px",
              height: 28,
              bgcolor: isDarkMode ? "rgba(99,102,241,.18)" : "rgba(79,70,229,.10)",
              color: isDarkMode ? "#A5B4FC" : "#4338CA",
              border: "1px solid",
              borderColor: isDarkMode ? "rgba(99,102,241,.4)" : "rgba(79,70,229,.25)",
            }}
          />
        );
      },
    },
    {
      headerName: "Date",
      field: "date",
      minWidth: 180,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = formatDate(params.value || params.data?.created_at || params.data?.updated_at);
        return (
          <Typography variant="body2" sx={{ color: isDarkMode ? "#94A3B8" : "#64748B" }}>
            {val}
          </Typography>
        );
      },
    },
    {
      headerName: "Update by",
      field: "update_by",
      minWidth: 150,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = params.value || params.data?.updatedBy || "-";
        return (
          <Typography variant="body2" sx={{ color: isDarkMode ? "#CBD5E1" : "#475569" }}>
            {val}
          </Typography>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      width: 90,
      minWidth: 90,
      maxWidth: 100,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: (params: any) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.data)} size="small" sx={{ color: secondaryText }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Card
        variant="outlined"
        sx={{
          height: "calc(100vh - 103px)",
          borderRadius: 3,
          width: "100%",
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: "100%",
              height: "calc(88vh - 90px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <SkeletonLoader
              count={Math.floor(window.innerHeight / 40)}
              styles={{ height: 30, width: "100%" }}
            />
          </Box>
        ) : (
          <AgGridTable
            columnDefs={columnDefs}
            data={masterFiles}
            showSearch
            searchPlaceholder="Search masterfiles..."
            height="calc(91.5vh - 90px)"
            isLoading={loading}
            onInviteSuccess={() => {
              setControlsFile(null);
              setParamsFile(null);
              setRulesFile(null);
              setWhatsNew("");
              setVersion("");
              setUploadType("controls");
              setIsModalOpen(true);
            }}
            InviteTableRefresh={fetchMasterFiles}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            borderRadius: "8px",
            mt: 0.5,
            minWidth: "120px"
          }
        }}
      >
        <MenuItem onClick={handleRollBack} sx={{ fontSize: "14px", color: "#101828" }}>
          Roll Back
        </MenuItem>
      </Menu>

      {/* Upload Masterfile Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 350 } as any}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1.5,
            backgroundColor: containerBg,
            color: primaryText,
            boxShadow: isDarkMode ? "0px 10px 30px rgba(0, 0, 0, 0.5)" : "0px 10px 30px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: "700",
            fontSize: "18px",
            color: primaryText,
            pb: 1.5,
            pt: 1,
            px: 2,
          }}
        >
          Upload Masterfile
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{
              color: secondaryText,
              "&:hover": {
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#F4F4F7",
                color: primaryText,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          px: 2, pb: 1, pt: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: isDarkMode ? "#27272A" : '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: isDarkMode ? "#52525B" : '#c1c1c1',
            borderRadius: '10px',
            '&:hover': {
              background: isDarkMode ? "#71717A" : '#a8a8a8',
            },
          },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <ToggleButtonGroup
              value={uploadType}
              exclusive
              onChange={(e, newType) => { if (newType) setUploadType(newType); }}
              size="small"
              sx={{
                backgroundColor: isDarkMode ? "#27272A" : "#F9FAFB",
                borderRadius: "8px",
                p: 0.5,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "6px !important",
                  px: 3,
                  py: 0.5,
                  textTransform: "none",
                  fontWeight: 600,
                  color: secondaryText,
                  "&.Mui-selected": {
                    backgroundColor: isDarkMode ? "#18181B" : "#ffffff",
                    color: activeColor,
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: primaryText,
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: isDarkMode ? "#18181B" : "#ffffff",
                  }
                }
              }}
            >
              <ToggleButton value="controls">Controls</ToggleButton>
              <ToggleButton value="params">Params</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Selected File Card or Upload Area (Primary) */}
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: primaryText }}>
            {uploadType === "controls" ? "Controls File (Excel/CSV)" : "Params File (Excel/CSV)"}
          </Typography>
          {(() => {
            const activeFile = uploadType === "controls" ? controlsFile : paramsFile;
            if (activeFile) {
              return (
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.25,
                    border: `1px dashed ${activeColor}`,
                    borderRadius: "10px",
                    backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.15)" : "rgba(58, 42, 218, 0.04)",
                    mb: 2,
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        backgroundColor: activeColor,
                        color: "#ffffff",
                        flexShrink: 0,
                      }}
                    >
                      <CloudUploadIcon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "600",
                          color: activeColor,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                        }}
                      >
                        {activeFile.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: secondaryText,
                          fontSize: "11px",
                        }}
                      >
                        {(activeFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={handleRemoveFile}
                    size="small"
                    sx={{
                      color: "#F87171",
                      backgroundColor: "transparent",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "rgba(239, 68, 68, 0.12)",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Paper>
              );
            }
            return (
              <Paper
                elevation={0}
                onClick={openFileBrowser}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  p: 1.5,
                  border: `1px dashed ${borderClr}`,
                  borderRadius: "10px",
                  backgroundColor: isDarkMode ? "#27272A" : "#F9FAFB",
                  mb: 2,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.08)" : "rgba(58, 42, 218, 0.02)",
                    borderColor: activeColor
                  }
                }}
              >
                <CloudUploadIcon sx={{ color: secondaryText, fontSize: 28 }} />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: primaryText }}>
                    Click to upload file
                  </Typography>
                  <Typography variant="caption" sx={{ color: secondaryText, mt: -0.2 }}>
                    CSV, XLSX or XLS
                  </Typography>
                </Box>
              </Paper>
            );
          })()}

          {/* Secondary File Upload Area (Rules) */}
          {uploadType === "params" && (
            <Box sx={{ mb: 2, mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: primaryText }}>
                Rules File (JSON)
              </Typography>
              {rulesFile ? (
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.25,
                    border: `1px dashed ${activeColor}`,
                    borderRadius: "10px",
                    backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.15)" : "rgba(58, 42, 218, 0.04)",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        backgroundColor: activeColor,
                        color: "#ffffff",
                        flexShrink: 0,
                      }}
                    >
                      <CloudUploadIcon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "600",
                          color: activeColor,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                        }}
                      >
                        {rulesFile.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: secondaryText,
                          fontSize: "11px",
                        }}
                      >
                        {(rulesFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setRulesFile(null)}
                    size="small"
                    sx={{
                      color: "#F87171",
                      backgroundColor: "transparent",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "rgba(239, 68, 68, 0.12)",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  {...getRulesRootProps()}
                  onClick={openRulesBrowser}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: isRulesDragActive ? `1px dashed ${activeColor}` : `1px dashed ${borderClr}`,
                    borderRadius: "10px",
                    backgroundColor: isRulesDragActive ? (isDarkMode ? "rgba(129, 140, 248, 0.1)" : "rgba(58, 42, 218, 0.02)") : (isDarkMode ? "#27272A" : "#F9FAFB"),
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.08)" : "rgba(58, 42, 218, 0.02)",
                      borderColor: activeColor
                    }
                  }}
                >
                  <input {...getRulesInputProps()} />
                  <CloudUploadIcon sx={{ color: secondaryText, fontSize: 28 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: primaryText }}>
                      Click to upload rules file
                    </Typography>
                    <Typography variant="caption" sx={{ color: secondaryText, mt: -0.2 }}>
                      JSON only
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {/* Form Fields */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              fullWidth
              label="What's New"
              placeholder="What's New"
              value={whatsNew}
              onChange={(e) => setWhatsNew(e.target.value)}
              disabled={isSaving}
              multiline
              rows={2}
              variant="outlined"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  color: primaryText,
                  "& fieldset": { borderColor: borderClr },
                  "&:hover fieldset": { borderColor: activeColor },
                  "&.Mui-focused fieldset": { borderColor: activeColor },
                },
                "& .MuiInputLabel-root": { color: secondaryText },
                "& .MuiInputLabel-root.Mui-focused": { color: activeColor },
              }}
            />

            <TextField
              fullWidth
              label="Version"
              placeholder="Version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              disabled={isSaving}
              variant="outlined"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  color: primaryText,
                  "& fieldset": { borderColor: borderClr },
                  "&:hover fieldset": { borderColor: activeColor },
                  "&.Mui-focused fieldset": { borderColor: activeColor },
                },
                "& .MuiInputLabel-root": { color: secondaryText },
                "& .MuiInputLabel-root.Mui-focused": { color: activeColor },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pt: 1.5, pb: 1, gap: 1.5, justifyContent: "center" }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            disabled={isSaving}
            sx={{
              flex: 1,
              color: activeColor,
              borderColor: activeColor,
              borderWidth: "1.5px",
              fontWeight: "600",
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              py: 1,
              "&:hover": {
                borderColor: activeHoverColor,
                borderWidth: "1.5px",
                backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.1)" : "rgba(58, 42, 218, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            sx={{
              flex: 1,
              backgroundColor: activeColor,
              color: "#ffffff",
              fontWeight: "600",
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              py: 1,
              boxShadow: isDarkMode ? "0px 4px 10px rgba(129, 140, 248, 0.25)" : "0px 4px 10px rgba(58, 42, 218, 0.15)",
              "&:hover": {
                backgroundColor: activeHoverColor,
                boxShadow: isDarkMode ? "0px 6px 15px rgba(129, 140, 248, 0.35)" : "0px 6px 15px rgba(58, 42, 218, 0.25)",
              },
            }}
          >
            {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterFile;
